import { averageScore } from '@/lib/scoreStatistics';
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Building2 } from 'lucide-react';

interface CompanyMetric {
  name: string;
  totalSessions: number;
  avgScore: number | null;
  teamSize: number;
  color: string;
}

const COLORS = [
  'hsl(var(--primary))',
  'hsl(var(--chart-2))',
  'hsl(var(--chart-3))',
  'hsl(var(--chart-4))',
  'hsl(var(--chart-5))',
];

export function CompanyComparisonChart() {
  const [data, setData] = useState<CompanyMetric[]>([]);
  const [loading, setLoading] = useState(true);
  const [metric, setMetric] = useState<'avgScore' | 'totalSessions' | 'teamSize'>('avgScore');

  useEffect(() => {
    async function fetchCompanyMetrics() {
      setLoading(true);
      try {
        // Fetch all companies
        const { data: companies } = await supabase
          .from('companies')
          .select('id, name')
          .eq('is_active', true);

        if (!companies || companies.length === 0) {
          setData([]);
          setLoading(false);
          return;
        }

        // Fetch team size per company
        const { data: profiles } = await supabase
          .from('profiles')
          .select('company_id')
          .not('company_id', 'is', null);

        // Fetch evaluation metrics per company
        const { data: evaluations } = await supabase
          .from('simulation_evaluations')
          .select('company_id, overall_score')
          .not('company_id', 'is', null)
          .eq('is_submitted', true);

        // Calculate metrics per company
        const companyMetrics: CompanyMetric[] = companies.map((company, index) => {
          const teamMembers = profiles?.filter(p => p.company_id === company.id) || [];
          const companyEvals = evaluations?.filter(e => e.company_id === company.id) || [];
          
          const totalSessions = companyEvals.length;
          const avgScore = averageScore(companyEvals);

          return {
            name: company.name.length > 15 ? company.name.substring(0, 15) + '...' : company.name,
            totalSessions,
            avgScore,
            teamSize: teamMembers.length,
            color: COLORS[index % COLORS.length],
          };
        });

        // Sort by the selected metric and take top 8
        const sorted = [...companyMetrics]
          .sort((a, b) => b[metric] - a[metric])
          .slice(0, 8);

        setData(sorted);
      } catch (error) {
        console.error('Error fetching company metrics:', error);
      } finally {
        setLoading(false);
      }
    }

    fetchCompanyMetrics();
  }, [metric]);

  const metricLabels = {
    avgScore: 'Avg Score (%)',
    totalSessions: 'Total Sessions',
    teamSize: 'Team Size',
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Company Comparison
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px] flex items-center justify-center">
            <div className="text-muted-foreground">Loading...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Company Comparison
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px] flex items-center justify-center">
            <p className="text-muted-foreground">No company data available</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Company Comparison
          </CardTitle>
          <div className="flex gap-1">
            {(['avgScore', 'totalSessions', 'teamSize'] as const).map((m) => (
              <button
                key={m}
                onClick={() => setMetric(m)}
                className={`px-3 py-1 text-xs rounded-md transition-colors ${
                  metric === m
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
                }`}
              >
                {m === 'avgScore' ? 'Score' : m === 'totalSessions' ? 'Sessions' : 'Team'}
              </button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={data} layout="vertical" margin={{ left: 20, right: 20 }}>
            <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={12} />
            <YAxis 
              type="category" 
              dataKey="name" 
              stroke="hsl(var(--muted-foreground))" 
              fontSize={11}
              width={100}
              tickLine={false}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'hsl(var(--card))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '8px',
              }}
              labelStyle={{ color: 'hsl(var(--foreground))' }}
              formatter={(value: number) => [
                `${value}${metric === 'avgScore' ? '%' : ''}`,
                metricLabels[metric]
              ]}
            />
            <Bar dataKey={metric} radius={[0, 4, 4, 0]}>
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
